import { createClient } from '@supabase/supabase-js';

const url = (import.meta.env.VITE_SUPABASE_URL || '').trim();
const key = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '').trim();

export const supabaseConfigured = Boolean(url && key);
export const supabase = supabaseConfigured
  ? createClient(url, key, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true } })
  : null;

export function normalizeRole(role) {
  if (role === 'admin' || role === 'administrator') return 'admin';
  if (role === 'instructor') return 'instructor';
  return 'student';
}

export async function loadProfile(userId) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}


export async function joinCourseByCode(courseCode) {
  if (!supabase) throw new Error('Supabase is not configured.');
  const code = String(courseCode || '').trim();
  if (!code) throw new Error('Course registration code is required.');
  const { data, error } = await supabase.rpc('join_course_by_code', { p_code: code });
  if (error) throw error;
  return data;
}

export async function ensurePendingCourseEnrollment(user) {
  if (!supabase || !user) return { joined: false, course: null };
  const code = String(user.user_metadata?.pending_course_code || '').trim();
  if (!code) return { joined: false, course: null };
  const data = await joinCourseByCode(code);
  return { joined: true, course: Array.isArray(data) ? (data[0] || null) : data };
}

export async function signIn(email, password) {
  if (!supabase) throw new Error('Supabase is not configured. Add the browser-safe environment values.');
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  if (!supabase) return;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function registerStudent({ name, studentId, email, password, courseCode }) {
  if (!supabase) throw new Error('Supabase is not configured.');
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { full_name: name, student_id: studentId, requested_role: 'student', pending_course_code: courseCode },
    },
  });
  if (error) throw error;

  if (data.session && courseCode) {
    await joinCourseByCode(courseCode);
  }
  return data;
}

export async function requestPasswordReset(email) {
  if (!supabase) throw new Error('Supabase is not configured.');
  const address = String(email || '').trim();
  if (!address) throw new Error('Email is required.');
  const { data, error } = await supabase.auth.resetPasswordForEmail(address, {
    redirectTo: window.location.origin,
  });
  if (error) throw error;
  return data;
}

export async function updatePassword(password) {
  if (!supabase) throw new Error('Supabase is not configured.');
  const value = String(password || '');
  if (value.length < 8) throw new Error('Password must contain at least 8 characters.');
  const { data, error } = await supabase.auth.updateUser({ password: value });
  if (error) throw error;
  return data;
}
